/**
 * The Orders page and Order drawer contracts.
 *
 * `GET /api/v1/orders/page` feeds the whole screen in one request and
 * `GET /api/v1/orders/:orderId/detail` feeds the whole drawer in one more. The shapes are
 * declared here rather than in either app so the server that produces them and the screen
 * that renders them are checked against one declaration: the orders module asserts its own
 * result types are identical to these (`domain/ordersPage.test.ts`), and the web imports
 * them directly. A field renamed on one side stops compiling on the other.
 *
 * Money is a plain integer here rather than the branded `Cents`, because the orders page
 * query builds its rows out of one SQL pass and the brand is enforced where the money
 * services live. The unit is still cents everywhere and there are no floats.
 *
 * Nothing on this page is computed by the client. The payment state, the matcher badge,
 * the attention reasons and the one contextual action are all decided server side, so the
 * table, the rail and the tiles cannot tell three different stories about one order.
 */
import { z } from 'zod';
import { AddressSchema, OrderQueueSchema, PaymentMethodSchema, PrintDocSchema } from './types.js';
/* ------------------------------------------------------------------ */
/* Small vocabularies                                                  */
/* ------------------------------------------------------------------ */
/** The four range chips. `all` has no lower bound. Applies to the placed date. */
export const OrderRangeSchema = z.enum(['7d', '30d', '90d', 'all']);
export const ORDER_RANGES = ['7d', '30d', '90d', 'all'];
/** The paid-state pill on a row. */
export const OrderPaymentStateSchema = z.enum(['paid', 'waiting', 'failed', 'refunded']);
/** The matcher badge. Null is an order the matcher never touched. */
export const OrderMatcherStateSchema = z.enum(['auto', 'sms', 'manual']).nullable();
/** The one contextual action in the last column. Decided server side, never re-derived. */
export const OrderQuickActionSchema = z.enum(['mark_paid', 'print', 'reprint', 'track', 'review']);
/** Why a row is in Needs attention. Each one renders as a chip on the row. */
export const OrderAttentionReasonSchema = z.enum([
    'compliance_hold',
    'address_flagged',
    'attempt_cluster',
    'writeback_dead',
    'payment_variance',
    'unmapped_payment_method',
    'exception_open',
    'failed_first_order',
    'stuck_awaiting_payment',
    'stuck_in_work',
    'stuck_packed',
]);
/** Owner tunable thresholds behind the queues, echoed so the screen can quote them. */
export const OrderQueueThresholdsSchema = z.object({
    windowDays: z.number().int(),
    stuckWorkDays: z.number().int(),
    stuckPackedDays: z.number().int(),
    awaitingPaymentHours: z.number().int(),
});
/** Where a row sits in the pipeline. `other` is a cancelled, refunded or failed order. */
export const OrderFulfilmentStateSchema = z.union([OrderQueueSchema, z.literal('other')]);
/* ------------------------------------------------------------------ */
/* The row                                                             */
/* ------------------------------------------------------------------ */
export const OrdersPageRowSchema = z.object({
    order_id: z.number().int(),
    number: z.string(),
    placed_at: z.string(),
    customer: z.object({
        name: z.string(),
        city: z.string().nullable(),
        state: z.string().nullable(),
    }),
    flags: z.object({
        first_order: z.boolean(),
        safearrival: z.boolean(),
        compliance_hold: z.boolean(),
        address_flagged: z.boolean(),
        attempt_cluster: z.boolean(),
    }),
    payment: z.object({
        method: PaymentMethodSchema,
        method_label: z.string(),
        state: OrderPaymentStateSchema,
        matcher: OrderMatcherStateSchema,
        reference: z.string().nullable(),
        autocancel_at: z.string().nullable(),
        autocancel_extendable: z.boolean(),
    }),
    total_cents: z.number().int(),
    refunded_cents: z.number().int(),
    item_count: z.number().int(),
    items_preview: z.array(z.object({ name: z.string(), qty: z.number().int() })),
    prints: z.object({
        slip_at: z.string().nullable(),
        coa_at: z.string().nullable(),
        label_at: z.string().nullable(),
    }),
    fulfilment: z.object({
        state: OrderFulfilmentStateSchema,
        packed_at: z.string().nullable(),
        photo_count: z.number().int(),
        carrier: z.string().nullable(),
        tracking: z.string().nullable(),
        tracking_url: z.string().nullable(),
        shipped_at: z.string().nullable(),
        delivered_at: z.string().nullable(),
    }),
    ops_state: z.string(),
    attention_reasons: z.array(OrderAttentionReasonSchema),
    quick_action: OrderQuickActionSchema,
});
/**
 * A payment the matcher could not attach to an order.
 *
 * It is not an order, so it is not an order row: it carries the amount, the payer and the
 * orders it might belong to, and the screen renders it distinctly on Needs attention and
 * All. Anything else would put a row in the table with no order behind it.
 */
export const UnmatchedPaymentRowSchema = z.object({
    kind: z.literal('unmatched_payment'),
    id: z.number().int(),
    source: z.string(),
    amount_cents: z.number().int(),
    payer: z.string().nullable(),
    memo: z.string().nullable(),
    received_at: z.string(),
    candidate_order_ids: z.array(z.number().int()),
    quick_action: z.literal('review'),
});
/* ------------------------------------------------------------------ */
/* The response                                                        */
/* ------------------------------------------------------------------ */
export const OrdersPageKpisSchema = z.object({
    paid_today: z.object({
        cents: z.number().int(),
        count: z.number().int(),
        yesterday_cents: z.number().int(),
        avg7_cents: z.number().int(),
    }),
    pipeline: z.object({
        awaiting_payment: z.number().int(),
        to_print: z.number().int(),
        to_pack: z.number().int(),
        packed: z.number().int(),
        in_transit: z.number().int(),
    }),
    problems: z.object({
        compliance_hold: z.number().int(),
        unmatched_payments: z.number().int(),
        address_flagged: z.number().int(),
        stuck: z.number().int(),
    }),
});
export const OrdersPageWidenedSchema = z.object({
    from: OrderRangeSchema,
    to: OrderRangeSchema,
    reason: z.literal('no_matches'),
});
export const OrdersPageResponseSchema = z.object({
    kpis: OrdersPageKpisSchema,
    queue_counts: z.record(OrderQueueSchema, z.number()),
    rows: z.array(OrdersPageRowSchema),
    unmatched_payments: z.array(UnmatchedPaymentRowSchema),
    next_cursor: z.string().nullable(),
    widened: OrdersPageWidenedSchema.nullable(),
    thresholds: OrderQueueThresholdsSchema,
});
/* ------------------------------------------------------------------ */
/* The drawer                                                          */
/* ------------------------------------------------------------------ */
export const CarrierSchema = z.enum(['ups', 'usps', 'fedex', 'dhl', 'other']);
export const TimelineKindSchema = z.enum([
    'note',
    'audit',
    'print',
    'pack',
    'status',
    'matcher',
    'payment',
    'shipment',
]);
export const TimelineVisibilitySchema = z.enum(['internal', 'customer']);
/** One merged entry: a Woo note, an ops audit row, a print ack, a pack or a status move. */
export const TimelineEntrySchema = z.object({
    id: z.string(),
    kind: TimelineKindSchema,
    at: z.string(),
    actor: z.string(),
    text: z.string(),
    visibility: TimelineVisibilitySchema,
});
export const DrawerMatcherStateSchema = z.enum(['auto_matched', 'sms_confirmed', 'manual', 'none']);
export const DrawerPhotoSchema = z.object({
    uuid: z.string(),
    thumb_url: z.string(),
    orig_url: z.string(),
    captured_at: z.string().nullable(),
});
export const OrderDrawerLineSchema = z.object({
    line_id: z.number().int(),
    product_id: z.number().int(),
    variation_id: z.number().int().nullable(),
    sku: z.string().nullable(),
    name: z.string(),
    qty: z.number().int(),
    unit_price_cents: z.number().int(),
    line_total_cents: z.number().int(),
    discount_cents: z.number().int(),
});
/**
 * `GET /orders/:orderId/detail`. Every section of the drawer, one read.
 *
 * Sections degrade individually: a MariaDB hiccup empties the timeline or drops the
 * auto-cancel deadline and leaves the rest, because this drawer is what an agent reads to
 * a customer on the phone.
 */
export const OrderDrawerDetailSchema = z.object({
    header: z.object({
        order_id: z.number().int(),
        order_number: z.string(),
        placed_at: z.string(),
        woo_status: z.string(),
        ops_state: z.string(),
        total_cents: z.number().int(),
        customer_name: z.string(),
        first_order: z.boolean(),
        safearrival: z.boolean(),
        version: z.string(),
    }),
    payment: z.object({
        method: PaymentMethodSchema,
        method_raw: z.string().nullable(),
        reference: z.string().nullable(),
        transaction_id: z.string().nullable(),
        paid_at: z.string().nullable(),
        matcher_state: DrawerMatcherStateSchema,
        matcher_note: z.string().nullable(),
        expected_cents: z.number().int().nullable(),
        received_cents: z.number().int().nullable(),
        payer_name: z.string().nullable(),
        unmatched_payment_id: z.number().int().nullable(),
        autocancel_at: z.string().nullable(),
        autocancel_extendable: z.boolean(),
        reminder_eligible: z.boolean(),
    }),
    fulfilment: z.object({
        print_acks: z.object({
            packing_slip_at: z.string().nullable(),
            coa_at: z.string().nullable(),
            label_at: z.string().nullable(),
            packing_slip_reprints: z.number().int(),
            coa_reprints: z.number().int(),
            label_reprints: z.number().int(),
        }),
        packed_at: z.string().nullable(),
        packed_by: z.string().nullable(),
        photos: z.array(DrawerPhotoSchema),
        carrier: CarrierSchema,
        carrier_raw: z.string().nullable(),
        tracking_number: z.string().nullable(),
        tracking_url: z.string().nullable(),
        shipped_at: z.string().nullable(),
        delivered_at: z.string().nullable(),
    }),
    items: z.object({
        lines: z.array(OrderDrawerLineSchema),
        coupon_codes: z.array(z.string()),
        subtotal_cents: z.number().int(),
        discount_cents: z.number().int(),
        shipping_cents: z.number().int(),
        tax_cents: z.number().int(),
        total_cents: z.number().int(),
        refunded_cents: z.number().int(),
    }),
    customer: z.object({
        customer_key: z.string(),
        name: z.string(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
        billing: AddressSchema.nullable(),
        shipping: AddressSchema.nullable(),
        lifetime: z.object({ orders: z.number().int(), spend_cents: z.number().int() }),
        rr_balance_cents: z.number().int().nullable(),
        address_editable: z.boolean(),
    }),
    notes_and_activity: z.array(TimelineEntrySchema),
    flags: z.array(z.object({ flag: z.string(), reason: z.string().nullable(), since: z.string() })),
    support_ticket_refs: z
        .array(z.object({
        public_ref: z.string(),
        status: z.string(),
        subject: z.string().nullable(),
        created_at: z.string(),
    }))
        .optional(),
});
/* ------------------------------------------------------------------ */
/* Reprint                                                             */
/* ------------------------------------------------------------------ */
/** The documents the reprint menu offers. */
export const REPRINT_DOCS = PrintDocSchema.options;
/** What one order's reprint answered with. The bulk fan-out keeps one of these per order. */
export const BulkReprintOutcomeSchema = z.object({
    order_id: z.number().int(),
    ok: z.boolean(),
    replayed: z.boolean(),
    accepted: z.array(PrintDocSchema),
    error: z.string().nullable(),
});
//# sourceMappingURL=ordersPage.js.map